import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export default function BlogEditor() {
  const [title, setTitle] = useState("");
  const quillRef = useRef(null);
  const quillInstance = useRef(null);
  const navigate = useNavigate();

  // Convert YouTube/Vimeo Links to Embedded Format
  const getEmbeddedVideoUrl = useCallback((url) => {
    const youtubeMatch = url.match(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/
    );
    const vimeoMatch = url.match(/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/);

    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    } else if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    return null;
  }, []);

  // Handle Image and Video Embedding via URL
  const handleEmbed = useCallback(
    (type) => {
      const url = prompt(`Enter ${type} URL:`);

      if (url) {
        const range = quillInstance.current.getSelection();

        if (type === "video") {
          const embedUrl = getEmbeddedVideoUrl(url);
          if (embedUrl) {
            quillInstance.current.insertEmbed(range.index, "video", embedUrl);
          } else {
            alert("Invalid video URL. Use a valid YouTube or Vimeo link.");
          }
        } else {
          quillInstance.current.insertEmbed(range.index, "image", url);
        }
      }
    },
    [getEmbeddedVideoUrl] // ✅ Dependency included
  );

  useEffect(() => {
    if (window.Quill && quillRef.current && !quillInstance.current) {
      quillInstance.current = new window.Quill(quillRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image", "video"],
            ["code-block"],
          ],
        },
      });

      const toolbar = quillInstance.current.getModule("toolbar");
      toolbar.addHandler("image", () => handleEmbed("image"));
      toolbar.addHandler("video", () => handleEmbed("video"));
    }
  }, [handleEmbed]); // ✅ Dependency added to avoid warning

  const handleSave = async () => {
    const blogData = {
      title: title,
      content: quillInstance.current.root.innerHTML,
      author: "Pavan's Badminton Academy",
    };

    try {
      const response = await fetch(
        "https://s2t2kdwqprvshgz7eed5ss6v6a0muiva.lambda-url.us-east-1.on.aws/upload-blog",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(blogData),
        }
      );

      if (response.ok) {
        alert("Blog saved successfully!");
        setTitle("");
        quillInstance.current.root.innerHTML = "";
      } else {
        console.error("Failed to save blog:", await response.text());
        alert("Failed to save blog. Please try again.");
      }
    } catch (error) {
      console.error("Error saving blog:", error);
      alert("An error occurred. Please check your network.");
    }
  };

  const handleCancel = () => {
    setTitle("");
    if (quillInstance.current) {
      quillInstance.current.root.innerHTML = "";
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white p-6 shadow-lg rounded-lg mt-10">
      {/* Blog Title */}
      <input
        type="text"
        placeholder="Enter blog title..."
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full p-3 border-b-2 text-2xl focus:outline-none"
      />

      {/* Quill Editor */}
      <div ref={quillRef} className="mt-4 h-64"></div>

      {/* Save & Cancel Buttons */}
      <div className="flex justify-end gap-4 mt-5">
        <button onClick={handleCancel} className="px-4 py-2 bg-red-500 text-white rounded">
          Cancel
        </button>
        <button
          onClick={() => navigate("/")}
          className="mt-8 bg-gray-800 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition"
        >
          Back to Dashboard
        </button>
        <button onClick={handleSave} className="px-4 py-2 bg-blue-500 text-white rounded">
          Save Blog
        </button>
      </div>
    </div>
  );
}
